import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Avatar, Skeleton, Tooltip, IconButton } from '@mui/material';
import { Person } from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { getGameUsers } from '../../services/api';

const AVATAR_MIN = 36;
const GRID_GAP = 8;

interface User {
  user_id: number;
  username: string;
  display_name?: string;
  avatar_url?: string;
  status: number;
  score?: number;
}

interface CommunitySectionProps {
  gameId: number;
  gameName?: string;
}

const UserAvatarLink = styled(Link)({
  textDecoration: 'none',
  position: 'relative',
  display: 'block',
  width: '100%',
  borderRadius: '50%',
  '&:hover .user-avatar': {
    transform: 'scale(1.1)',
    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
  }
});

const UserAvatar = styled(Avatar)({
  width: '100%',
  height: 'auto',
  aspectRatio: '1 / 1',
  transition: 'all 0.2s ease',
  border: '0px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: '#424242',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  }
});

const StatusIndicator = styled(Box)<{ status: number }>(({ theme, status }) => {
  const getStatusColor = (s: number) => {
    switch (s) {
      case 1: return theme.palette.primary.main;
      case 2: return theme.palette.info.main;
      case 3: return theme.palette.warning.main;
      case 4: return '#b99a71';
      case 5: return theme.palette.secondary.main;
      default: return '#757575';
    }
  };
  return {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: getStatusColor(status),
    border: '2px solid #121212',
  };
});

const CommunitySection: React.FC<CommunitySectionProps> = ({ gameId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [cols, setCols] = useState(8);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchGameUsers = async () => {
      try {
        setLoading(true);
        const data = await getGameUsers(gameId);
        setUsers(data.users || []);
        setTotalCount(data.total_count || 0);
      } catch (error) {
        console.error('Error fetching game users:', error);
      } finally {
        setLoading(false);
      }
    };
    if (gameId) fetchGameUsers();
  }, [gameId]);

  // ローディング中から観測し、コンテナ幅から auto-fill と同じ列数を計算する
  useEffect(() => {
    if (!wrapperRef.current) return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      const computed = Math.max(1, Math.floor((width + GRID_GAP) / (AVATAR_MIN + GRID_GAP)));
      setCols(computed);
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return 'プレイ済み';
      case 2: return 'プレイ中';
      case 3: return '中断';
      case 4: return '積みゲー';
      case 5: return 'ウィッシュ';
      default: return '';
    }
  };

  const getScoreText = (score?: number) => score ? ` ★${score}` : '';

  if (loading) {
    return (
      <Box ref={wrapperRef} sx={{ mb: 3, mt: { xs: 4, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Skeleton variant="text" width={80} height={32} />
          <Skeleton variant="text" width={30} height={24} sx={{ ml: 1 }} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(36px, 1fr))', gap: 1 }}>
          {[...Array(16)].map((_, i) => (
            <Skeleton key={i} variant="circular" sx={{ width: '100%', height: 'auto', aspectRatio: '1 / 1' }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (users.length === 0) return null;

  // auto-fill と同じ列数に基づいて末尾の端数を切り捨て（最大3行分）
  const displayCount = users.length >= cols
    ? Math.min(Math.floor(users.length / cols) * cols, cols * 3)
    : users.length;
  const displayUsers = users.slice(0, displayCount);

  return (
    <Box ref={wrapperRef} sx={{ mb: 3, mt: { xs: 4, sm: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: 'text.primary' }}>
          コミュニティ
        </Typography>
        <Box sx={{
          ml: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          fontSize: '0.7rem',
          padding: '2px 8px',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
        }}>
          {totalCount}
        </Box>
        <IconButton
          component={Link}
          to={`/game/${gameId}/community?tab=members`}
          size="small"
          sx={{ ml: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>

      <Box
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(36px, 1fr))', gap: '8px', mt: 2 }}
      >
        {displayUsers.map((user) => (
          <Tooltip
            key={user.user_id}
            arrow
            title={
              <>
                {user.display_name || user.username || `User ${user.user_id}`}
                <br />
                {getStatusText(user.status)}{getScoreText(user.score)}
              </>
            }
          >
            <UserAvatarLink to={`/profile/${user.username || user.user_id}`}>
              <UserAvatar
                className="user-avatar"
                src={user.avatar_url || undefined}
                alt={user.display_name || user.username || `User ${user.user_id}`}
              >
                {!user.avatar_url && <Person />}
              </UserAvatar>
              <StatusIndicator status={user.status} />
            </UserAvatarLink>
          </Tooltip>
        ))}
      </Box>

      {totalCount > displayCount && (
        <Link to={`/game/${gameId}/community?tab=members`} style={{ textDecoration: 'none' }}>
          <Typography
            variant="body2"
            sx={{ mt: 1.5, color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', '&:hover': { color: 'rgba(255, 255, 255, 0.8)' } }}
          >
            {totalCount}人が記録しています →
          </Typography>
        </Link>
      )}
    </Box>
  );
};

export default CommunitySection;
